import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import LionsStripe from '../components/LionsStripe';
import {
  NewspaperIcon,
  CalendarDaysIcon,
  AcademicCapIcon,
  BuildingLibraryIcon,
  ComputerDesktopIcon,
  ChatBubbleLeftRightIcon,
  PencilSquareIcon,
  WifiIcon,
  ArrowDownTrayIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline';

// To publish a new month: drop the new PDF into /public/assets/documents,
// then update ISSUE and the highlights below.
const ISSUE = {
  label: 'November 2026',
  url: '/assets/documents/november-2026-nycps-family-update.pdf',
  downloadName: 'NYCPS-Monthly-Update-November-2026.pdf',
  meta: 'PDF · 2 pages · 266 KB'
};

const ALL_UPDATES_URL = 'https://www.schools.nyc.gov/about-us/messages-for-families';

const importantDates = [
  { date: 'Nov 3', text: 'Election Day, school will be in session remotely for all students' },
  { date: 'Nov 5', text: 'Afternoon/Evening Parent-Teacher Conferences (PTCs) for elementary schools*' },
  { date: 'Nov 11', text: 'Veterans Day, schools closed' },
  { date: 'Nov 12', text: 'Afternoon/Evening PTCs for middle schools and D75 schools*' },
  { date: 'Nov 19', text: 'Evening PTCs for high schools, K–12, and 6–12 schools' },
  { date: 'Nov 20', text: 'Afternoon PTCs for high schools, K–12, and 6–12 schools*' },
  { date: 'Nov 26–27', text: 'Thanksgiving Recess, schools closed' }
];

const ExternalLink = ({ href, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="inline-flex items-center gap-1 text-orange-700 hover:text-orange-800 font-semibold underline underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
  >
    {children}
    <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" aria-hidden="true" />
    <span className="sr-only">(opens in a new tab)</span>
  </a>
);

const highlights = [
  {
    icon: ComputerDesktopIcon,
    title: 'Prepare for Remote Learning on Election Day',
    audience: 'All students',
    body: (
      <p>
        Election Day on <strong>Tuesday, November 3</strong> will be a remote school day for all
        students. Ensure your child has a working, internet-enabled device at home and that they
        know how to access their NYCPS Student Account before the remote learning day. If you need
        help, visit SelfService.schools.nyc or ask your school.
      </p>
    ),
    links: [
      { href: 'https://schools.nyc.gov/StudentAccounts', label: 'Student Accounts' },
      { href: 'https://selfservice.schools.nyc', label: 'SelfService.schools.nyc' }
    ]
  },
  {
    icon: ChatBubbleLeftRightIcon,
    title: 'November Parent-Teacher Conferences',
    audience: 'All families',
    body: (
      <>
        <p>
          The next round of parent-teacher conferences (PTCs) begins on{' '}
          <strong>Thursday, November 5</strong>. Visit schools.nyc.gov/PTC for tips and guidance on
          what to expect and how to get the most out of this opportunity to connect with your
          child&rsquo;s teachers!
        </p>
        <p>
          Note: individual schools&rsquo; conference dates might be different from the dates
          noted. Your child&rsquo;s teacher will work with you to schedule your conference.
        </p>
      </>
    ),
    links: [{ href: 'https://schools.nyc.gov/PTC', label: 'Conference tips' }]
  },
  {
    icon: BuildingLibraryIcon,
    title: 'Upcoming Middle and High School Admissions Deadlines',
    audience: 'Middle and high school admissions',
    body: (
      <>
        <p>
          The high school application will close on <strong>Friday, December 4</strong>, and the
          middle school application will close on <strong>Friday, December 11</strong>.
        </p>
        <p>
          For programs requiring auditions, you must submit your audition materials by the
          respective application deadlines on MySchools.nyc.
        </p>
      </>
    ),
    links: [{ href: 'https://schools.nyc.gov/Admissions', label: 'Admissions' }]
  },
  {
    icon: PencilSquareIcon,
    title: 'SHSAT and Mark Twain-District 21 Talent Test',
    audience: 'Middle and high school admissions',
    body: (
      <>
        <p>
          The deadline to register for the SHSAT is <strong>October 30</strong>. This year&rsquo;s
          test will once again be administered in a digital format. You will receive your test
          ticket with your exact test date in November, with testing scheduled later in the month.
        </p>
        <p>
          The deadline to register for the Mark Twain-District 21 Talent Test is{' '}
          <strong>November 2</strong>. You will receive your test ticket with your exact test date
          in December, with testing scheduled to begin in January.
        </p>
      </>
    ),
    links: [
      { href: 'https://schools.nyc.gov/SHSAT', label: 'SHSAT' },
      { href: 'https://schools.nyc.gov/MSAuditions', label: 'Talent Test' }
    ]
  },
  {
    icon: AcademicCapIcon,
    title: 'Kindergarten Admissions Opens December 9',
    audience: 'Children born in 2022',
    body: (
      <>
        <p>
          Beginning <strong>Tuesday, December 9</strong>, NYC children who were born in 2022 can
          participate in kindergarten admissions for fall 2027. You can add Gifted and Talented
          (G&amp;T) and Dual Language programs to your child&rsquo;s application at MySchools.nyc;
          G&amp;T eligibility will be determined after you apply.
        </p>
        <p>
          The fall 2027 kindergarten application will close on{' '}
          <strong>Friday, January 22, 2027</strong>.
        </p>
      </>
    ),
    links: [{ href: 'https://schools.nyc.gov/K', label: 'Kindergarten admissions' }]
  },
  {
    icon: WifiIcon,
    title: 'Free Tech Resources for Families',
    audience: 'Students and families',
    body: (
      <p>
        The City&rsquo;s Office of Technology and Innovation provides free technology resources,
        including Wi-Fi, computer access, digital skills training, printing, and software access to
        support students and families year-round.
      </p>
    ),
    links: [{ href: 'https://nyc.gov/GetOnline', label: 'Free resources near you' }]
  }
];

const MonthlyUpdatesPage = () => {
  return (
    <div className="min-h-screen bg-accent-light font-sans selection:bg-secondary selection:text-white">
      <Navbar />

      <main id="main-content">
        {/* ============================================================================
            HERO SECTION
        ============================================================================ */}
        <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 bg-primary overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-br from-primary via-navy-900 to-primary"></div>
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-secondary/10 rounded-full blur-[100px]"></div>
            <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-blue-400/10 rounded-full blur-[100px]"></div>
          </div>

          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center bg-primary rounded-2xl py-1">
            <div className="inline-flex items-center justify-center p-3 bg-white/10 backdrop-blur rounded-full mb-8 border border-white/20">
              <NewspaperIcon className="w-8 h-8 text-white" aria-hidden="true" />
            </div>
            <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6 tracking-tight">
              NYCPS <span className="text-secondary">Monthly Updates</span>
            </h1>
            <p className="text-xl text-white/90 font-light mb-3">
              Latest update: {ISSUE.label}
            </p>
            <p className="text-white/70 font-light max-w-2xl mx-auto">
              Important dates, admissions deadlines, parent-teacher conferences, and more for
              families, shared by New York City Public Schools.
            </p>

            <div className="flex flex-wrap gap-4 justify-center mt-10">
              <a
                href={ISSUE.url}
                download={ISSUE.downloadName}
                className="bg-orange-700 hover:bg-orange-800 text-white px-8 py-4 rounded-full font-semibold transition-all duration-300 shadow-[0_0_20px_rgba(244,101,36,0.3)] hover:shadow-[0_0_30px_rgba(244,101,36,0.5)] hover:-translate-y-1 inline-flex items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-primary"
              >
                <ArrowDownTrayIcon className="w-5 h-5" aria-hidden="true" />
                Download the Update (PDF)
              </a>
              <a
                href={ISSUE.url}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/30 text-white px-8 py-4 rounded-full font-semibold transition-all duration-300 hover:-translate-y-1 inline-flex items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-primary"
              >
                Open in a New Tab
                <ArrowTopRightOnSquareIcon className="w-5 h-5" aria-hidden="true" />
                <span className="sr-only">(opens in a new tab)</span>
              </a>
            </div>

            <p className="text-white/60 text-sm mt-6">{ISSUE.meta}</p>
          </div>
          <LionsStripe className="absolute inset-x-0 bottom-0 z-10" />
        </section>

        {/* ============================================================================
            IMPORTANT DATES
        ============================================================================ */}
        <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 -mt-12 relative z-20">
          <div className="max-w-5xl mx-auto">
            <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
              <div className="bg-primary px-8 py-6">
                <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                  <CalendarDaysIcon className="w-7 h-7 text-secondary" aria-hidden="true" />
                  Important Upcoming Dates
                </h2>
              </div>

              <ul className="p-4 sm:p-8 space-y-3">
                {importantDates.map((item) => (
                  <li
                    key={item.date}
                    className="flex items-start gap-4 p-4 rounded-2xl bg-gray-50 border border-gray-100"
                  >
                    <span className="flex-shrink-0 w-20 text-center py-1.5 rounded-xl bg-white border border-gray-200 font-bold text-primary text-sm">
                      {item.date}
                    </span>
                    <span className="text-gray-800 pt-1">{item.text}</span>
                  </li>
                ))}
              </ul>
              <p className="px-8 pb-8 -mt-2 text-sm text-gray-700">
                *Students in these schools are dismissed three hours early.
              </p>
            </div>
          </div>
        </section>

        {/* ============================================================================
            HIGHLIGHTS
        ============================================================================ */}
        <section className="pb-16 md:pb-24 px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">This Month&rsquo;s Highlights</h2>

            <div className="grid md:grid-cols-2 gap-6">
              {highlights.map((item) => (
                <article
                  key={item.title}
                  className="bg-white rounded-3xl shadow-lg border border-gray-100 p-6 sm:p-8 flex flex-col"
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div className="p-3 rounded-xl bg-gray-50 border border-gray-200 text-primary flex-shrink-0">
                      <item.icon className="w-6 h-6" aria-hidden="true" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 text-lg leading-tight">{item.title}</h3>
                      <p className="text-sm text-orange-700 font-semibold mt-1">{item.audience}</p>
                    </div>
                  </div>

                  <div className="space-y-3 text-gray-700 leading-relaxed flex-1">{item.body}</div>

                  <ul className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-sm">
                    {item.links.map((link) => (
                      <li key={link.href}>
                        {link.internal ? (
                          <a
                            href={link.href}
                            className="text-orange-700 hover:text-orange-800 font-semibold underline underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
                          >
                            {link.label}
                          </a>
                        ) : (
                          <ExternalLink href={link.href}>{link.label}</ExternalLink>
                        )}
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ============================================================================
            EMBEDDED READER
        ============================================================================ */}
        <section className="pb-16 md:pb-24 px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
              <div className="px-8 py-6 border-b border-gray-100 flex flex-wrap items-center justify-between gap-4">
                <h2 className="text-2xl font-bold text-gray-900">Read the Full Update</h2>
                <a
                  href={ISSUE.url}
                  download={ISSUE.downloadName}
                  className="text-sm font-bold text-orange-700 hover:text-orange-800 inline-flex items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
                >
                  <ArrowDownTrayIcon className="w-4 h-4" aria-hidden="true" />
                  Download a copy
                </a>
              </div>

              <object
                data={ISSUE.url}
                type="application/pdf"
                className="w-full h-[600px] md:h-[800px] bg-gray-100"
                aria-label={`NYCPS monthly update for ${ISSUE.label}, embedded PDF reader`}
              >
                <div className="p-8 text-center">
                  <p className="text-gray-700 mb-4">
                    Your browser can&rsquo;t display the update here. You can still read it by
                    downloading the PDF.
                  </p>
                  <a
                    href={ISSUE.url}
                    download={ISSUE.downloadName}
                    className="inline-flex items-center gap-2 bg-orange-700 hover:bg-orange-800 text-white px-6 py-3 rounded-full font-bold transition-colors"
                  >
                    <ArrowDownTrayIcon className="w-5 h-5" aria-hidden="true" />
                    Download the Update (PDF)
                  </a>
                </div>
              </object>
            </div>

            {/* More from NYCPS */}
            <div className="mt-8 bg-primary text-white rounded-3xl shadow-xl p-8 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-3xl -mr-10 -mt-10"></div>
              <div className="relative z-10">
                <h2 className="text-xl font-bold mb-2">Keep up with NYC Public Schools</h2>
                <p className="text-white/80 mb-6 max-w-2xl">
                  Find every message for families on the NYCPS website, or bookmark The Morning Bell
                  blog for the latest news and resources.
                </p>
                <div className="flex flex-wrap gap-4">
                  <a
                    href={ALL_UPDATES_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white text-primary px-6 py-3 rounded-full font-bold hover:bg-gray-100 transition-colors inline-flex items-center gap-2"
                  >
                    All messages for families
                    <ArrowTopRightOnSquareIcon className="w-4 h-4" aria-hidden="true" />
                    <span className="sr-only">(opens in a new tab)</span>
                  </a>
                  <a
                    href="https://schools.nyc.gov/MorningBell"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white/10 hover:bg-white/20 border border-white/30 px-6 py-3 rounded-full font-bold transition-colors inline-flex items-center gap-2"
                  >
                    The Morning Bell
                    <ArrowTopRightOnSquareIcon className="w-4 h-4" aria-hidden="true" />
                    <span className="sr-only">(opens in a new tab)</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default MonthlyUpdatesPage;
